import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AboutLink } from "@/pages/About";
import { LegalLinks } from "@/components/Legal";
import { AuthLayout } from "@/components/AuthLayout";
import { Input } from "@/components/ui-kit";
import { Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth, homeFor } from "@/contexts/AuthContext";
import { ForgotPasswordWhatsApp } from "@/components/PlatformContact";

export default function PartnerLogin() {
  const { t } = usePrefs();
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { username: email.trim(), password });
      await refreshMe();
      navigate(homeFor(data.role), { replace: true });
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.partnerTitle")} sub={t("auth.partnerSub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="partner-login-form">
        <div className="space-y-2">
          <Label htmlFor="username">{t("auth.username")}</Label>
          <Input id="username" dir="ltr" type="text" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-username-input" className="h-12 rounded-2xl" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" dir="ltr" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-12 rounded-2xl" />
        </div>
        {error && <p data-testid="login-error" className="text-sm text-primary font-semibold">{error}</p>}
        <button disabled={busy} data-testid="login-submit-button" className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 disabled:opacity-60 active:scale-[0.98] transition-[transform,background-color]">
          {busy ? t("common.loading") : t("auth.submit")}
        </button>
      </form>
      <div className="text-sm space-y-2">
        <ForgotPasswordWhatsApp username={email} />
        <p>
          {t("auth.noAccount")}{" "}
          <Link to="/partner/register" data-testid="goto-register-link" className="text-primary font-bold">{t("auth.registerLink")}</Link>
        </p>
        <Link to="/login" data-testid="goto-customer-login-link" className="text-muted-foreground font-semibold">{t("auth.customerLink")}</Link>
        <div><AboutLink /></div>
        <LegalLinks />
      </div>
    </AuthLayout>
  );
}
